import React from "react";

import { isEmpty, some, toLower } from "lodash/fp";

import { SearchCompatibleAnnotatedText } from "@app/analytics/components/AnnotatedText";
import {
  BulletedList,
  BulletedListItem,
  EmptyNote,
  NoteSubSection,
} from "@app/analytics/components/reports/styles";
import TimestampChip from "@app/analytics/MeetingReport/components/TimestampChip";
import UpsellOverlay from "@app/analytics/MeetingReport/components/UpsellOverlay";
import type {
  NoteItem,
  NoteItem as INoteItem,
  NoteSearchResult,
} from "@app/analytics/MeetingReport/types";
import { chunkNoteBySearchResults } from "@app/analytics/MeetingReport/utils";
import type {
  NoteType,
  SessionNote,
  TranscriptSummaryItem,
} from "@app/common/types";
import { titleize } from "@app/utils/utils";

import ExpandableList from "@rui/ExpandableList";
import { Span } from "@rui/styles";

import { BulletedListItemContainer } from "../styles";

const DEFAULT_MAX_COLLAPSED_LIST_ITEMS = 3;
const EXPANDABLE_LIST_BUTTON_PROPS = {
  viewMoreLabel: "View more",
  viewLessLabel: "View less",
  sx: { mt: 1, ml: 1 },
};

const TIMESTAMPS_ONLY_ON_HOVER_SX = {
  "& .timestamp-chip": {
    visibility: "hidden",
  },
  "&:hover": {
    "& .timestamp-chip": {
      visibility: "visible",
    },
  },
};

const isPrivateNote = (item: TranscriptSummaryItem | SessionNote) =>
  Boolean("private" in item && item.private);

const ListItemWithTimestamp = ({
  children,
  onClick,
  isPrivate = false,
  timestamp,
  timestampsOnHover,
}: {
  children: React.ReactNode;
  isPrivate?: boolean;
  onClick?: () => void;
  timestamp?: string;
  timestampsOnHover?: boolean;
}) => {
  if (!timestamp) {
    return (
      <BulletedListItemContainer>
        <BulletedListItem sx={{ whiteSpace: "pre-wrap" }}>
          {children}
        </BulletedListItem>
      </BulletedListItemContainer>
    );
  }

  return (
    <BulletedListItemContainer isPrivate={isPrivate}>
      <BulletedListItem
        onClick={onClick}
        sx={timestampsOnHover ? TIMESTAMPS_ONLY_ON_HOVER_SX : undefined}
      >
        <Span sx={{ whiteSpace: "pre-wrap" }}>{children}</Span>
        <TimestampChip className="timestamp-chip" sx={{ ml: 1 }}>
          {timestamp}
        </TimestampChip>
      </BulletedListItem>
    </BulletedListItemContainer>
  );
};

const NoteItem = ({
  isPrivate,
  item,
  onClick,
  searchResults = [],
  timestampsOnHover,
}: {
  isPrivate: boolean;
  item: INoteItem;
  onClick: (item: INoteItem) => (() => void) | undefined;
  searchResults?: NoteSearchResult[];
  timestampsOnHover?: boolean;
}) => {
  if (isEmpty(searchResults)) {
    return (
      <ListItemWithTimestamp
        isPrivate={isPrivate}
        timestamp={item.timestamp}
        onClick={onClick(item)}
        timestampsOnHover={timestampsOnHover}
      >
        {item.value}
      </ListItemWithTimestamp>
    );
  }

  const chunks = chunkNoteBySearchResults(item.value, searchResults);

  return (
    <ListItemWithTimestamp
      isPrivate={isPrivate}
      timestamp={item.timestamp}
      onClick={onClick(item)}
      timestampsOnHover={timestampsOnHover}
    >
      {chunks.map((chunk) => (
        <SearchCompatibleAnnotatedText
          key={chunk.uniqueKey}
          highlighted={chunk.highlighted}
          searchIndex={chunk.highlightIndex}
        >
          {chunk.content}
        </SearchCompatibleAnnotatedText>
      ))}
    </ListItemWithTimestamp>
  );
};

const AnnotatedNoteSubSection = ({
  isNotetakerReport,
  items,
  maxItems,
  onItemClick,
  searchResults,
  timestampsOnHover,
}: {
  isNotetakerReport: boolean;
  items: INoteItem[];
  maxItems: number;
  onItemClick: (item: INoteItem) => (() => void) | undefined;
  searchResults: Map<string, NoteSearchResult[]>;
  timestampsOnHover?: boolean;
}) => {
  // force the list to expand if any of the search results appear below the fold
  const maxItemsToShow = items
    .slice(DEFAULT_MAX_COLLAPSED_LIST_ITEMS)
    .some((item) => !isEmpty(searchResults.get(item.id)))
    ? items.length
    : maxItems;

  return (
    <NoteSubSection>
      <BulletedList>
        <ExpandableList
          buttonProps={EXPANDABLE_LIST_BUTTON_PROPS}
          max={maxItemsToShow}
        >
          {items.map((item) => (
            <NoteItem
              key={item.id}
              isPrivate={isNotetakerReport && isPrivateNote(item)}
              item={item}
              onClick={onItemClick}
              searchResults={searchResults.get(item.id)}
              timestampsOnHover={timestampsOnHover}
            />
          ))}
        </ExpandableList>
      </BulletedList>
    </NoteSubSection>
  );
};

export interface NoteSubSectionWithAnnotationsProps {
  isNotetakerReport?: boolean;
  items: NoteItem[];
  maxItems?: number;
  onItemClick: (item: INoteItem) => (() => void) | undefined;
  searchResults: Map<string, NoteSearchResult[]>;
  shouldUpsell?: boolean;
  timestampsOnHover?: boolean;
  type: NoteType;
}

const NoteSubSectionWithAnnotations = ({
  isNotetakerReport = false,
  items,
  maxItems = DEFAULT_MAX_COLLAPSED_LIST_ITEMS,
  onItemClick,
  shouldUpsell = false,
  searchResults,
  timestampsOnHover = false,
  type,
}: NoteSubSectionWithAnnotationsProps) => {
  if (shouldUpsell) {
    return (
      <NoteSubSection>
        <UpsellOverlay>
          <BulletedList>
            {items.map((item) => (
              <ListItemWithTimestamp
                key={item.id}
                timestamp={item.timestamp}
                timestampsOnHover={timestampsOnHover}
              >
                {item.value}
              </ListItemWithTimestamp>
            ))}
          </BulletedList>
        </UpsellOverlay>
      </NoteSubSection>
    );
  }

  if (isEmpty(items)) {
    const typeDisplayName = toLower(titleize(type));

    return (
      <NoteSubSection>
        <EmptyNote>
          {type === "note"
            ? "You did not take any notes in this meeting"
            : `No ${typeDisplayName}s${isNotetakerReport ? "" : " generated"}`}
        </EmptyNote>
      </NoteSubSection>
    );
  }

  const hasSearchResults = some(
    (item) =>
      !isEmpty(
        searchResults.get((item as TranscriptSummaryItem | SessionNote).id)
      ),
    items
  );

  if (hasSearchResults) {
    return (
      <AnnotatedNoteSubSection
        isNotetakerReport={isNotetakerReport}
        items={items}
        maxItems={maxItems}
        onItemClick={onItemClick}
        searchResults={searchResults}
        timestampsOnHover={timestampsOnHover}
      />
    );
  }

  const maxItemsToShow = isNotetakerReport ? items.length : maxItems;

  return (
    <NoteSubSection>
      <BulletedList>
        <ExpandableList
          buttonProps={EXPANDABLE_LIST_BUTTON_PROPS}
          max={maxItemsToShow}
        >
          {items.map((item) => (
            <ListItemWithTimestamp
              key={item.id}
              isPrivate={isNotetakerReport && isPrivateNote(item)}
              onClick={onItemClick(item)}
              timestamp={item.timestamp}
              timestampsOnHover={timestampsOnHover}
            >
              {item.value}
            </ListItemWithTimestamp>
          ))}
        </ExpandableList>
      </BulletedList>
    </NoteSubSection>
  );
};

export default NoteSubSectionWithAnnotations;
